<template>
    <div
        class="w-full inline-flex items-center gap-1 text-slate-500 text-sm font-medium whitespace-nowrap"
        @click="handleClick"
        :class="{
            'cursor-pointer': sortable,
            'justify-start': justify === 'start',
            'justify-end': justify === 'end',
            'justify-center': justify === 'center',
        }"
    >
        <p class="font-['Inter'] leading-tight">
            {{ label }}
        </p>
        <Icon
            v-if="isActive && sortable"
            icon="arrow-up"
            class="w-4 h-4 transition-transform duration-200"
            :class="{
                'transform rotate-180': sortDirection === 'desc',
            }"
        />
    </div>
</template>

<script setup>
/**
 * SortableColumnHeader Component
 *
 * A reusable component for table column headers that support sorting functionality.
 * The component displays a label and conditionally shows a sort direction indicator
 * when the column is active. Clicking on a sortable header will cycle through
 * ascending and descending sort orders.
 *
 * @emits {string, string} sort - Emitted when header is clicked with (sortDirection, columnLabel)
 * @example
 * <SortableColumnHeader
 *   label="Name"
 *   :isActive="sortBy === 'name'"
 *   :sortable="true"
 *   @sort="(direction, column) => handleSort(direction, column)"
 * />
 */
import { ref, watch } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    label: {
        type: String,
        required: true,
    },
    /**
     * Whether this column can be sorted
     * When false, clicking the header will not trigger sorting and no sort icon will appear
     */
    sortable: {
        type: Boolean,
        default: false,
    },
    /**
     * Whether this column is currently the active sort column
     * When true, the sort direction icon will be displayed
     */
    isActive: {
        type: Boolean,
        default: false,
    },
    /**
     * The key to use for sorting
     * This is the property name in the data object that the column represents
     */
    sortKey: {
        type: String,
        required: false,
    },
    /**
     * Text alignment for the column header
     * Can be 'start', 'center', or 'end'
     */
    justify: {
        type: String,
        default: 'start',
        validator: (value) => ['start', 'center', 'end'].includes(value),
    },
    /**
     * Current sort direction from parent (for syncing state)
     * Can be 'asc' or 'desc'
     */
    currentDirection: {
        type: String,
        default: 'asc',
        validator: (value) => ['asc', 'desc'].includes(value),
    },
});

const sortDirection = ref(props.currentDirection);

const emit = defineEmits(['sort']);
/**
 * Handles click events on the header
 * - If not sortable, does nothing
 * - If not active, (i.e., on first click) sets sort to ascending
 * - If active and ascending, (i.e., on second click) changes to descending
 * - If active and descending, (i.e., on third click) changes back to ascending
 * Then emits the sort event with the new direction and column label
 */
const handleClick = () => {
    if (!props.sortable) return;
    if (!props.isActive) {
        sortDirection.value = 'asc';
    } else if (sortDirection.value === 'asc') {
        sortDirection.value = 'desc';
    } else {
        sortDirection.value = 'asc';
    }
    emit('sort', sortDirection.value, props.sortKey);
};

/**
 * Watches for changes to the isActive prop
 * When isActive changes to false, reset sort direction to ascending
 */
watch(
    () => props.isActive,
    (newVal) => {
        sortDirection.value = newVal ? sortDirection.value : 'asc';
    }
);

/**
 * Watches for changes to the currentDirection prop
 * Syncs internal state with parent's sort direction
 */
watch(
    () => props.currentDirection,
    (newVal) => {
        if (props.isActive) {
            sortDirection.value = newVal;
        }
    }
);
</script>
